import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import type { Selector } from 'react-redux';
import {
  selectNetworkErrorLog,
  selectAgentErrorsLog,
  selectTeamNetworkErrorLog,
  selectTeamsIdsWithTargetService,
  selectTeamAgentErrors,
} from 'selectors';
import { makeHeaderCells, convertNativeDateToExpectedFormat } from 'utils';
import { setFilters } from 'actionCreators';
import {
  fetchNetworkErrorLog,
  fetchTeamNetworkErrorLog,
  fetchTeamAgentErrorLog,
  fetchAgentErrorLog,
} from 'thunk';
import * as constants from '@constants';
import { format } from 'date-fns';
import { FilterType, NetworkErrorsLogType, HeadCell, RecentErrorsType } from 'types';
import useResetGlobalState from 'hooks/useResetGlobalState';

const defaultDate = format(new Date(), 'yyyy-MM-dd');

const noop = () => {};

const noopGetters = {
  getHeadCells: () => [],
  getData: () => ({
    formattedData: [],
    filter: {},
    fetchedSoFar: null,
    total: null,
  }),
  getOnPageChange: noop,
  getOnRequestSort: noop,
  getFetchNew: noop,
  getOnDateChange: noop,
};

type ViewByTypes = 'general' | 'team';
type ErrorType = 'network-errors' | 'agent-errors';

type DataSelectorReturnType = {
  formattedData: NetworkErrorsLogType[];
  filter: FilterType;
  fetchedSoFar: number;
  total: number;
};

const headCellsByErrorType = {
  [constants.AGENT]: [
    {
      id: 'created_at',
      label: 'Created At',
      tooltip: 'The time at which the request was created at',
      tooltipPlacement: 'top',
    },
    {
      id: 'updated_at',
      label: 'Updated At',
      tooltip: 'The time at which the request was updated at',
      tooltipPlacement: 'top',
    },
    {
      id: 'id',
      label: 'ID',
      tooltip: 'Unique request ID',
      tooltipPlacement: 'top',
    },
    {
      id: 'mosaic_team_id',
      label: 'Team ID',
      tooltip: 'Mosaic team ID',
      tooltipPlacement: 'top',
    },
    {
      id: 'target_service_id',
      label: 'Integration ID',
      tooltip: 'Target service ID (unique Integration ID)',
      tooltipPlacement: 'top',
    },
    {
      id: 'data_type',
      label: 'Data Type',
      tooltip: 'Type of data that has error',
      tooltipPlacement: 'top',
    },
    {
      id: 'session_id',
      label: 'Session ID',
      tooltip:
        'Unique ID given to data being uploaded, which is the same for all entries uploaded at the same time',
      tooltipPlacement: 'top',
    },
    {
      id: 'message',
      label: 'Message',
      tooltip: 'The error message from the Agent (include this when reporting errors)',
      tooltipPlacement: 'top',
    },
  ],
  [constants.NETWORK]: [
    {
      id: 'createdAt',
      label: 'Created At',
      tooltip: 'The time at which the request was created at',
      tooltipPlacement: 'top',
    },
    {
      id: 'id',
      label: 'ID',
      tooltip: 'Unique request ID',
      tooltipPlacement: 'top',
    },
    {
      id: 'mosaicTeamId',
      label: 'Team ID',
      tooltip: 'Mosaic team ID',
      tooltipPlacement: 'top',
    },
    {
      id: 'targetServiceId',
      label: 'Integration ID',
      tooltip: 'Target service ID (unique Integration ID)',
      tooltipPlacement: 'top',
    },
    {
      id: 'dataType',
      label: 'Data Type',
      tooltip: 'Type of data that has error',
      tooltipPlacement: 'top',
    },
    {
      id: 'entityId',
      label: 'Entity ID',
      tooltip: 'Unique request ID, same as ID (include this when reporting errors)',
      tooltipPlacement: 'top',
      shouldStopPropagationOnClick: true,
    },
    {
      id: 'sessionId',
      label: 'Session ID',
      tooltip:
        'Unique ID given to data being uploaded, which is the same for all entries uploaded at the same time',
      tooltipPlacement: 'top',
    },
    {
      id: 'apiRequestId',
      label: 'API Request ID',
      tooltip:
        'Unique HTTP Request ID sent from the app to the service (include this when reporting errors)',
      tooltipPlacement: 'top',
      shouldStopPropagationOnClick: true,
    },
    {
      id: 'error',
      label: 'Error',
      tooltip: 'The error message from the Integration Server (include this when reporting errors)',
      tooltipPlacement: 'top',
    },
  ],
};

type ViewByHashType = Record<
  ViewByTypes,
  Record<
    ErrorType,
    {
      getData: () => DataSelectorReturnType;
      getHeadCells: (formattedData: NetworkErrorsLogType[]) => HeadCell[];
      getFetchNew: (filter: FilterType) => () => void;
      getOnPageChange: (newPage: number, filter: FilterType, fetchedSoFar: number) => () => void;
      getOnRequestSort: (filter: FilterType, property: string) => () => void;
      getOnDateChange: (newStartDate: string, filter: FilterType) => () => void;
    }
  >
>;

type HookProps = {
  targetServiceName: string;
  viewBy: ViewByTypes;
  mosaicTeamId?: number; // For team specific only
  targetServiceId?: number; // For team specific recent errors
};

type RecordStringBoolean = Record<string, boolean>;

const hasFetchedInitialState = {
  [constants.NETWORK]: false,
  [constants.AGENT]: false,
};

const isAllCollapseInitialState = {
  [constants.NETWORK]: true,
  [constants.AGENT]: true,
};

const isFirstTimeAllCollapseInitialState = {
  [constants.NETWORK]: true,
  [constants.AGENT]: true,
};

const useErrorLogs = ({ targetServiceName, viewBy, mosaicTeamId, targetServiceId }: HookProps) => {
  const mosaicTeamIds = useAppSelector(selectTeamsIdsWithTargetService(targetServiceName));
  const dispatch = useAppDispatch();
  const { resetGeneralIntegrationErrorLog, resetIntegrationErrorLog } = useResetGlobalState();
  const [date, setDate] = useState(defaultDate);
  const [_isAllCollapse, _setIsAllCollapse] =
    useState<RecordStringBoolean>(isAllCollapseInitialState);
  const [_isFirstTimeAllCollapse, _setIsFirstTimeAllCollapse] = useState<RecordStringBoolean>(
    isFirstTimeAllCollapseInitialState,
  );

  const [errorType, setErrorType] = useState(constants.defaultErrorType);

  const isAllCollapse = _isAllCollapse[errorType.value];
  const isFirstTimeAllCollapse = _isFirstTimeAllCollapse[errorType.value];

  const setIsAllCollapse = (errorType: string, value: boolean) => {
    _setIsAllCollapse({
      ..._isAllCollapse,
      [errorType]: value,
    });
  };

  const setIsFirstTimeAllCollapse = (errorType: string, value: boolean) => {
    _setIsFirstTimeAllCollapse({
      ..._isFirstTimeAllCollapse,
      [errorType]: value,
    });
  };

  const resetCollapseState = () => {
    _setIsFirstTimeAllCollapse(isFirstTimeAllCollapseInitialState);
    _setIsAllCollapse(isAllCollapseInitialState);
  };

  // Network error logs - IS
  const networkErrorLogs = useAppSelector(selectNetworkErrorLog);
  const teamNetworkErrorLogs = useAppSelector(selectTeamNetworkErrorLog);

  // Agent errors errors - Agent
  const agentErrorLogs = useAppSelector(selectAgentErrorsLog);
  const teamAgentErrorLogs = useAppSelector(selectTeamAgentErrors);
  useEffect(() => {
    // resetCollapseState();
    resetGeneralIntegrationErrorLog();
    resetIntegrationErrorLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetServiceName]);

  const resetErrorStateHash = {
    [constants.GENERAL]: resetGeneralIntegrationErrorLog,
    [constants.TEAM]: resetIntegrationErrorLog,
  };

  const resetErrorState = resetErrorStateHash[viewBy];

  /** Fix any */
  const viewByHash: ViewByHashType | any = useMemo(() => {
    return {
      [constants.GENERAL]: {
        [constants.NETWORK]: {
          getData: () => networkErrorLogs,
          getHeadCells: (formattedData: NetworkErrorsLogType[]) =>
            headCellsByErrorType[constants.NETWORK],

          getFetchNew: (filter: FilterType) => () => {
            dispatch(
              fetchNetworkErrorLog({
                targetService: targetServiceName,
                mosaicTeamIds: mosaicTeamIds,
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
                limit: filter.limit,
                offset: 0,
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
          },
          getOnPageChange: (newPage: number, filter: FilterType, fetchedSoFar: number) => () => {
            const newOffset = newPage * filter.limit;
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: newOffset,
                page: newPage,
              }),
            );
            if (fetchedSoFar <= newOffset) {
              dispatch(
                fetchNetworkErrorLog({
                  targetService: targetServiceName,
                  mosaicTeamIds: mosaicTeamIds,
                  isInitialFetch: false,
                  startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
                  limit: filter.limit,
                  offset: newOffset,
                }),
              );
            }
          },
          getOnRequestSort: (filter: FilterType, property: string) => () => {
            const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
            dispatch(
              setFilters({
                filterId: filter.id,
                page: 0,
                orderBy: property,
                orderDirection: isAsc ? 'desc' : 'asc',
              }),
            );
          },
          getOnDateChange: (newStartDate: string, filter: FilterType) => () => {
            // if (!isFirstTimeAllCollapse) {
            dispatch(
              fetchNetworkErrorLog({
                targetService: targetServiceName,
                mosaicTeamIds: mosaicTeamIds,
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(
                  newStartDate,
                  constants.standardDateFormat,
                ),
                limit: filter.limit,
                offset: 0,
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
            // }
            setDate(newStartDate);
          },
        },
        [constants.AGENT]: {
          getData: () => agentErrorLogs,
          getHeadCells: (formattedData: RecentErrorsType[]) =>
            headCellsByErrorType[constants.AGENT],

          getFetchNew: (filter: FilterType) => () => {
            dispatch(
              fetchAgentErrorLog({
                targetService: targetServiceName,
                agentOnly: targetServiceName === 'quickbooks-desktop',
                limit: filter.limit,
                offset: 0,
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
          },
          getOnPageChange: (newPage: number, filter: FilterType, fetchedSoFar: number) => () => {
            const newOffset = newPage * filter.limit;
            if (fetchedSoFar <= newOffset) {
              dispatch(
                fetchAgentErrorLog({
                  orderBy: filter.orderBy,
                  orderDirection: filter.orderDirection,
                  targetService: targetServiceName,
                  limit: filter.limit,
                  offset: newOffset,
                  startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
                }),
              );
            }
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: newOffset,
                page: newPage,
              }),
            );
          },
          getOnRequestSort: (filter: FilterType, property: string) => () => {
            const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
                limit: filter.limit,
                orderBy: property,
                orderDirection: isAsc ? 'desc' : 'asc',
              }),
            );
          },
          getOnDateChange: (newStartDate: string, filter: FilterType) => () => {
            // if (!isFirstTimeAllCollapse) {
            dispatch(
              fetchAgentErrorLog({
                offset: 0,
                targetService: targetServiceName,
                agentOnly: targetServiceName === 'quickbooks-desktop',
                limit: filter.limit,
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(
                  newStartDate,
                  constants.standardDateFormat,
                ),
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
            // }
            setDate(newStartDate);
          },
        },
      },
      [constants.TEAM]: {
        [constants.NETWORK]: {
          getData: () => teamNetworkErrorLogs,
          getHeadCells: (formattedData: NetworkErrorsLogType[]) =>
            headCellsByErrorType[constants.NETWORK],

          getFetchNew: (filter: FilterType) => () => {
            dispatch(
              fetchTeamNetworkErrorLog({
                targetService: targetServiceName,
                mosaicTeamIds: [mosaicTeamId as number],
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
                limit: filter.limit,
                offset: 0,
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
          },
          getOnPageChange: (newPage: number, filter: FilterType, fetchedSoFar: number) => () => {
            const newOffset = newPage * filter.limit;
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: newOffset,
                page: newPage,
              }),
            );
            if (fetchedSoFar <= newOffset) {
              dispatch(
                fetchTeamNetworkErrorLog({
                  targetService: targetServiceName,
                  mosaicTeamIds: [mosaicTeamId as number],
                  isInitialFetch: false,
                  startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
                  limit: filter.limit,
                  offset: newOffset,
                }),
              );
            }
          },
          getOnRequestSort: (filter: FilterType, property: string) => () => {
            const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
            dispatch(
              setFilters({
                filterId: filter.id,
                page: 0,
                orderBy: property,
                orderDirection: isAsc ? 'desc' : 'asc',
              }),
            );
          },
          getOnDateChange: (newStartDate: string, filter: FilterType) => () => {
            // if (!isFirstTimeAllCollapse) {
            dispatch(
              fetchTeamNetworkErrorLog({
                targetService: targetServiceName,
                mosaicTeamIds: [mosaicTeamId as number],
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(
                  newStartDate,
                  constants.standardDateFormat,
                ),
                limit: filter.limit,
                offset: 0,
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
            // }
            setDate(newStartDate);
          },
        },
        [constants.AGENT]: {
          getData: () => teamAgentErrorLogs,
          getHeadCells: (formattedData: RecentErrorsType[]) =>
            headCellsByErrorType[constants.AGENT],
          getFetchNew: (filter: FilterType) => () => {
            dispatch(
              fetchTeamAgentErrorLog({
                offset: 0,
                targetService: targetServiceName,
                targetServiceId: targetServiceId as number,
                agentOnly: targetServiceName === 'quickbooks-desktop',
                limit: filter.limit,
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
          },
          getOnPageChange: (newPage: number, filter: FilterType, fetchedSoFar: number) => () => {
            const newOffset = newPage * filter.limit;
            if (fetchedSoFar <= newOffset) {
              dispatch(
                fetchTeamAgentErrorLog({
                  orderBy: filter.orderBy,
                  orderDirection: filter.orderDirection,
                  targetService: targetServiceName,
                  targetServiceId: targetServiceId as number,
                  limit: filter.limit,
                  offset: newOffset,
                  startDate: convertNativeDateToExpectedFormat(date, constants.standardDateFormat),
                }),
              );
            }
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: newOffset,
                page: newPage,
              }),
            );
          },
          getOnRequestSort: (filter: FilterType, property: string) => () => {
            const isAsc = filter.orderBy === property && filter.orderDirection === 'asc';
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
                limit: filter.limit,
                orderBy: property,
                orderDirection: isAsc ? 'desc' : 'asc',
              }),
            );
          },
          getOnDateChange: (newStartDate: string, filter: FilterType) => () => {
            // if (!isFirstTimeAllCollapse) {
            dispatch(
              fetchTeamAgentErrorLog({
                offset: 0,
                targetService: targetServiceName,
                targetServiceId: targetServiceId as number,
                agentOnly: targetServiceName === 'quickbooks-desktop',
                limit: filter.limit,
                isInitialFetch: true,
                startDate: convertNativeDateToExpectedFormat(
                  newStartDate,
                  constants.standardDateFormat,
                ),
              }),
            );
            dispatch(
              setFilters({
                filterId: filter.id,
                offset: 0,
                page: 0,
              }),
            );
            // }
            setDate(newStartDate);
          },
        },
      },
    };
  }, [
    agentErrorLogs,
    date,
    dispatch,
    mosaicTeamId,
    mosaicTeamIds,
    networkErrorLogs,
    targetServiceId,
    targetServiceName,
    teamAgentErrorLogs,
    teamNetworkErrorLogs,
  ]);

  const { getHeadCells, getData, getOnPageChange, getOnRequestSort, getFetchNew, getOnDateChange } =
    useMemo(() => {
      const { value } = errorType;

      if (!value) return noopGetters;

      return viewByHash[viewBy][value];
    }, [errorType, viewBy, viewByHash]);

  const { formattedData, filter, fetchedSoFar, total, error, isRequesting } = getData();
  const fetchNew = getFetchNew ? getFetchNew(filter) : noop;

  useEffect(() => {
    if (errorType.value && fetchNew) {
      fetchNew();
    }
    // Spec: Refetch data when error type change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorType]);

  const formattedHeadCells = useMemo(() => getHeadCells(), [getHeadCells]);

  const onPageChange = useCallback(
    (_: any, newPage: number) => {
      const onPageChangeAction = getOnPageChange
        ? getOnPageChange(newPage, filter, fetchedSoFar)
        : noop;
      onPageChangeAction();
    },
    [fetchedSoFar, filter, getOnPageChange],
  );

  const onRequestSort = useCallback(
    (_: any, property: string) => {
      const onRequestSortAction = getOnRequestSort ? getOnRequestSort(filter, property) : noop;
      onRequestSortAction();
    },
    [filter, getOnRequestSort],
  );

  const onAllCollapse = useCallback(() => {
    if (isAllCollapse && isFirstTimeAllCollapse) {
      const fetchNew = getFetchNew ? getFetchNew(filter) : noop;
      fetchNew();
    }
  }, [filter, getFetchNew, isAllCollapse, isFirstTimeAllCollapse]);

  const onDateChange = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      // Reset redux state
      resetErrorState();

      if (!errorType.value) {
        setDate(event.target.value);
        // resetCollapseState();
        return;
      }
      const onDateChangeAction = getOnDateChange
        ? getOnDateChange(event.target.value, filter)
        : noop;

      // Persist the collapse state of the currently selected error type
      _setIsFirstTimeAllCollapse({
        ...isFirstTimeAllCollapseInitialState,
        [errorType.value]: isFirstTimeAllCollapse,
      });
      _setIsAllCollapse({
        ...isAllCollapseInitialState,
        [errorType.value]: isAllCollapse,
      });
      onDateChangeAction();
    },
    [
      errorType.value,
      filter,
      getOnDateChange,
      isAllCollapse,
      isFirstTimeAllCollapse,
      resetErrorState,
    ],
  );

  const onErrorTypeSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedErrorTypeValue = event.target.value;
    setErrorType(constants.errorTypesHash[selectedErrorTypeValue]);
  };

  const filtersToShow = useMemo(
    () => ({
      sections: ['singleValuePicker', 'datePicker'],
      filtersProps: {
        singleValuePicker: {
          item: errorType,
          items: constants.errorTypes,
          inputLabel: 'Log Type',
          onValueChange: onErrorTypeSelect,
        },
        datePicker: {
          defaultDate: date,
          onDayChange: onDateChange,
        },
      },
    }),
    [date, onDateChange, errorType, onErrorTypeSelect],
  );

  return {
    isLoading: isRequesting,
    error,
    data: formattedData,
    total,
    filterState: filter,
    headCells: formattedHeadCells,
    onRequestSort,
    onPageChange,
    onAllCollapse,
    filtersToShow,
    errorType,
    fetchNew,
    collapseProps: {
      isAllCollapse,
      isFirstTimeAllCollapse,
      setIsAllCollapse,
      setIsFirstTimeAllCollapse,
    },
  };
};

export default useErrorLogs;
